export interface User {
  id: string;
  walletAddress: string;
  walletAddressHash?: string; // Hash de l'adresse pour sécurité
  username?: string;
  displayName?: string;
  bio?: string;
  profilePicture?: string;
  bannerImage?: string;
  xp: number;
  level: number;
  badges: Badge[];
  socialLinks: SocialLinks;
  isVerified: boolean;
  role: 'user' | 'moderator' | 'admin';
  following: string[]; // IDs des utilisateurs suivis
  followers: string[]; // IDs des utilisateurs qui nous suivent
  followersCount: number;
  followingCount: number;
  createdAt: Date;
  updatedAt?: Date;
  dataHashedAt?: Date; // Timestamp du dernier hashage des données
}

export interface SocialLinks {
  twitter?: string;
  twitterHash?: string; // Hash sécurisé du compte Twitter complet
  telegram?: string;
  telegramHash?: string; // Hash sécurisé du compte Telegram complet
  website?: string;
  discord?: string;
  discordHash?: string; // Hash sécurisé du compte Discord complet
}

export interface Badge {
  id: string;
  name: string;
  description: string;
  icon: string;
  rarity: 'common' | 'rare' | 'epic' | 'legendary';
  unlockedAt: Date;
}

export interface Post {
  id: string;
  authorId: string;
  content: string;
  mediaUrls?: string[]; // Images, GIFs, Vidéos
  mediaTypes?: MediaType[]; // Types des médias correspondants
  links?: string[]; // Liens externes
  category: 'feedback' | 'meme' | 'research' | 'launchpad' | 'general' | 'dao' | 'annonces';
  likes: string[];
  comments: Comment[];
  shares: number;
  views: number;
  isDeleted: boolean;
  isPinned: boolean;
  tags: string[];
  mentionedTokens?: TokenInfo[];
  mentionedUsers?: string[]; // IDs des utilisateurs mentionnés
  poll?: Poll; // Sondage attaché au post
  isScheduled?: boolean;
  scheduledFor?: Date;
  // Thread functionality
  isThread: boolean; // Indique si ce post fait partie d'un thread
  threadId?: string; // ID du thread parent (pour les posts du thread)
  threadOrder?: number; // Ordre dans le thread (0 = post principal)
  threadParts?: ThreadPart[]; // Parties du thread (seulement pour le post principal)
  threadLength?: number; // Nombre total de parties dans le thread
  createdAt: Date;
  updatedAt?: Date;
}

export interface ThreadPart {
  id: string;
  content: string;
  mediaUrls?: string[];
  mediaTypes?: MediaType[]; // Types des médias correspondants
  order: number; // Position dans le thread (1, 2, 3...)
  createdAt: Date;
}

// Types de médias supportés
export type MediaType = 'image' | 'video' | 'gif';

// Interface pour les métadonnées de média
export interface MediaFile {
  file: File;
  type: MediaType;
  url?: string; // URL de prévisualisation
  thumbnail?: string; // URL de la miniature (pour les vidéos)
  duration?: number; // Durée en secondes (pour les vidéos)
}

// Interface pour les données de post sécurisées
export interface SecurePostData {
  contentHash?: string; // Hash du contenu pour vérification d'intégrité
  mediaHashes?: string[]; // Hash des URLs de média
  shareHistory?: ShareEvent[]; // Historique sécurisé des partages
}

export interface ShareEvent {
  userId: string;
  timestamp: Date;
  dataHash: string; // Hash pour vérification d'intégrité
}

export interface Comment {
  id: string;
  postId: string;
  authorId: string;
  content: string;
  likes: string[];
  isDeleted: boolean;
  createdAt: Date;
  updatedAt?: Date;
  // Système de réponses
  parentId?: string; // ID du commentaire parent (null pour commentaire principal)
  replies?: Comment[]; // Réponses à ce commentaire
  replyCount?: number; // Nombre de réponses
}

// Interface pour les données de commentaire sécurisées
export interface SecureCommentData {
  contentHash?: string; // Hash du contenu pour détecter modifications
  authorHash?: string; // Hash de l'auteur pour audit
}

export interface TokenInfo {
  address: string;
  symbol: string;
  name: string;
  decimals: number;
  logoURI?: string;
  price?: number;
  priceChange24h?: number;
  volume24h?: number;
  marketCap?: number;
  circulatingSupply?: number;
  totalSupply?: number;
  socialLinks?: SocialLinks;
  verified?: boolean;
}

export interface TradeData {
  inputMint: string;
  outputMint: string;
  amount: number;
  slippageBps: number;
}

export interface SearchResult {
  users: User[];
  posts: Post[];
  tokens: TokenInfo[];
}

// Types pour les tokens
export interface Token {
  id: string;
  name: string;
  symbol: string;
  amount: number;
  value: number;
  change24h: number;
  logoUrl: string;
}

// Types pour les NFTs
export interface NFT {
  id: string;
  name: string;
  imageUrl: string;
  collection: string;
  price?: number;
  currency?: string;
  createdAt: number;
  ownerId: string;
}

export interface Conversation {
  id: string;
  participants: string[];
  lastMessage?: Message;
  lastMessageTimestamp?: any;
  unreadCount: number;
  createdAt: any;
  updatedAt?: any;
}

export interface Message {
  id: string;
  senderId: string;
  receiverId?: string;
  content: string;
  type?: 'text' | 'image' | 'gif' | 'nft' | 'token';
  isRead: boolean;
  createdAt: Date;
  timestamp?: any;
}

export interface NFTInfo {
  mintAddress: string;
  name: string;
  image: string;
  collection?: string;
  attributes: Array<{
    trait_type: string;
    value: string;
  }>;
  floorPrice?: number;
}

export type XPAction = 'post' | 'like' | 'comment' | 'share' | 'daily_login' | 'first_trade' | 'token_discovery';

export const XP_REWARDS: Record<XPAction, number> = {
  post: 10,
  like: 1,
  comment: 5,
  share: 3,
  daily_login: 5,
  first_trade: 50,
  token_discovery: 15
};

export const LEVELS = [
  { level: 1, minXP: 0, title: 'Débutant', badge: '🌱' },
  { level: 2, minXP: 100, title: 'Apprenti', badge: '📈' },
  { level: 3, minXP: 250, title: 'Trader', badge: '💎' },
  { level: 4, minXP: 500, title: 'Expert', badge: '🚀' },
  { level: 5, minXP: 1000, title: 'Maître', badge: '👑' },
  { level: 6, minXP: 2500, title: 'Légende', badge: '⭐' },
  { level: 7, minXP: 5000, title: 'Alpha Hunter', badge: '🔥' },
  { level: 8, minXP: 10000, title: 'Jupiter Master', badge: '🌟' }
];

export interface Activity {
  id: string;
  type: 'like' | 'comment' | 'follow' | 'post' | 'token_discovery';
  actorId: string; // ID de l'utilisateur qui fait l'action
  targetId?: string; // ID de l'utilisateur cible (pour follow)
  postId?: string; // ID du post concerné
  tokenSymbol?: string; // Symbole du token (pour token_discovery)
  content?: string; // Contenu optionnel
  createdAt: Date;
  actorUsername?: string; // Cache du nom d'utilisateur pour éviter des requêtes
  actorDisplayName?: string;
}

export interface PollOption {
  id: string;
  text: string;
  votes: string[]; // IDs des utilisateurs qui ont voté pour cette option
  voteHashes?: string[]; // Hash des votes pour audit de sécurité
}

export interface Poll {
  id: string;
  question: string;
  options: PollOption[];
  duration: number; // en heures
  allowMultipleChoice: boolean;
  createdAt: Date;
  expiresAt: Date;
  totalVotes: number;
  voters: string[]; // IDs des utilisateurs qui ont voté
  voteIntegrityHash?: string; // Hash d'intégrité pour audit
}

export interface ScheduledPost {
  id: string;
  authorId: string;
  content: string;
  mediaUrls?: string[];
  category: 'feedback' | 'meme' | 'research' | 'launchpad' | 'general' | 'dao' | 'annonces';
  poll?: Poll;
  scheduledFor: Date;
  createdAt: Date;
  status: 'scheduled' | 'published' | 'cancelled';
}

// =====================================
// 🎙️ SPACES TYPES
// =====================================

export interface Space {
  id: string;
  postId?: string; // Lié à un post spécifique (pour l'intégration PostCard)
  title: string;
  description?: string;
  hostId: string;
  isLive: boolean;
  isPublic: boolean;
  maxParticipants: number;
  participants: string[]; // userIds de tous les participants
  speakers: string[]; // userIds avec permissions de parole
  listeners: string[]; // userIds en mode écoute seulement
  tags: string[];
  scheduledFor?: Date;
  startedAt?: Date;
  endedAt?: Date;
  recording?: {
    isRecorded: boolean;
    url?: string;
  };
  createdAt: Date;
  updatedAt?: Date;
}

export interface SpaceParticipant {
  spaceId: string;
  userId: string;
  role: 'host' | 'speaker' | 'listener';
  isMuted: boolean;
  isAudioOn: boolean;
  isHandRaised: boolean;
  joinedAt: Date;
  lastActiveAt: Date;
  peerId?: string; // WebRTC peer ID
}

export interface SpaceMessage {
  id: string;
  spaceId: string;
  authorId: string;
  content: string;
  type: 'text' | 'emoji' | 'system';
  createdAt: Date;
}

export interface CreateSpaceRequest {
  title: string;
  description?: string;
  postId?: string;
  isPublic: boolean;
  maxParticipants: number;
  tags: string[];
  scheduledFor?: Date;
}

// Extension du type Post pour inclure les Spaces
export interface PostWithSpace extends Post {
  spaceId?: string;
  hasActiveSpace?: boolean;
  spaceParticipants?: number;
}
